/**
 * 长按确认按钮：高风险操作（强制通过 / 强制确认）必须按住 N 秒才触发，
 * 防误触。按住期间按钮背景进度条 + 倒计时；中途松手 / 失焦即重置。
 *
 * 原实现来自结算台账的 ConfirmableRiskWarningModal，抽成公共组件供 UCM 强制通过等复用。
 */

import React, { useEffect, useRef, useState } from "react";
import { Button } from "antd";

export const HOLD_CONFIRM_DURATION_MS = 5000;

const HoldToConfirmButton = ({
    children,
    duration = HOLD_CONFIRM_DURATION_MS,
    active = true,
    disabled = false,
    loading = false,
    onConfirm,
    size,
    danger = true,
    type = "primary",
    block = false,
    icon = null,
    minWidth = 180,
}) => {
    const [progress, setProgress] = useState(0);
    const timerRef = useRef(null);
    const startTimeRef = useRef(null);
    const confirmingRef = useRef(false);
    const mountedRef = useRef(true);
    const activeRef = useRef(active);
    const disabledRef = useRef(disabled);
    const loadingRef = useRef(loading);

    activeRef.current = active;
    disabledRef.current = disabled;
    loadingRef.current = loading;

    const clearTimer = () => {
        if (timerRef.current) {
            clearInterval(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        return () => {
            mountedRef.current = false;
            clearTimer();
        };
    }, []);

    useEffect(() => {
        if (!active || disabled || loading) {
            clearTimer();
            startTimeRef.current = null;
            setProgress(0);
        }
    }, [active, disabled, loading]);

    const resetHold = (options = {}) => {
        if (confirmingRef.current && !options.force) return;
        clearTimer();
        startTimeRef.current = null;
        setProgress(0);
    };

    const completeHold = () => {
        if (confirmingRef.current || !activeRef.current || disabledRef.current || loadingRef.current) return;
        confirmingRef.current = true;
        clearTimer();
        setProgress(1);
        Promise.resolve(onConfirm?.()).finally(() => {
            confirmingRef.current = false;
            if (mountedRef.current) {
                setProgress(0);
            }
        });
    };

    const startHold = (event) => {
        if (!activeRef.current || disabledRef.current || loadingRef.current || confirmingRef.current || timerRef.current) return;
        event.preventDefault();
        clearTimer();
        startTimeRef.current = Date.now();
        setProgress(0.01);
        timerRef.current = setInterval(() => {
            if (!activeRef.current || disabledRef.current || loadingRef.current) {
                resetHold({ force: true });
                return;
            }
            const elapsed = Date.now() - startTimeRef.current;
            const nextProgress = Math.min(elapsed / duration, 1);
            setProgress(nextProgress);
            if (nextProgress >= 1) {
                completeHold();
            }
        }, 50);
    };

    const secondsLeft = Math.max(0, Math.ceil((duration * (1 - progress)) / 1000));
    const buttonLabel = progress > 0 ? `继续按住 ${secondsLeft}s` : children;

    return (
        <Button
            type={type}
            danger={danger}
            size={size}
            block={block}
            icon={progress > 0 ? null : icon}
            disabled={disabled || !active}
            loading={loading}
            onClick={(event) => event.preventDefault()}
            onPointerDown={startHold}
            onPointerUp={() => resetHold()}
            onPointerLeave={() => resetHold()}
            onPointerCancel={() => resetHold()}
            onKeyDown={(event) => {
                if (event.key === " " || event.key === "Enter") {
                    startHold(event);
                }
            }}
            onKeyUp={(event) => {
                if (event.key === " " || event.key === "Enter") {
                    resetHold();
                }
            }}
            style={{
                minWidth,
                backgroundImage: progress > 0
                    ? `linear-gradient(90deg, rgba(82,196,26,0.38) ${progress * 100}%, transparent ${progress * 100}%)`
                    : undefined,
            }}
        >
            {buttonLabel}
        </Button>
    );
};

export default HoldToConfirmButton;
